import { useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';
import type { SpinsInfo, SpinResult, WheelPrizeConfig } from '@/types/gems';

export const useWheelSpin = () => {
  const { user } = useAuth();
  const [spinsInfo, setSpinsInfo] = useState<SpinsInfo | null>(null);
  const [prizes, setPrizes] = useState<WheelPrizeConfig[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSpinsInfo = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const { data, error } = await supabase.rpc('get_daily_spins_remaining', {
        p_user_id: user.id
      });

      if (error) {
        debugLogger.error('WheelSpin', 'Error fetching spins info:', error);
        return;
      }

      setSpinsInfo(data as unknown as SpinsInfo);
      debugLogger.debug('WheelSpin', 'Spins info fetched:', data);
    } catch (error) {
      debugLogger.error('WheelSpin', 'Error fetching spins info:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchPrizes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('wheel_prizes_config')
        .select('*')
        .eq('is_active', true)
        .order('slot_number');

      if (error) {
        debugLogger.error('WheelSpin', 'Error fetching prizes:', error);
        return;
      }

      setPrizes(data as WheelPrizeConfig[]);
    } catch (error) {
      debugLogger.error('WheelSpin', 'Error fetching prizes:', error);
    }
  }, []);

  const spin = useCallback(async (useGems: boolean = false, adminTest: boolean = false): Promise<SpinResult | null> => {
    if (!user?.id) {
      toast.error('Você precisa estar logado para girar a roleta');
      return null;
    }

    if (isSpinning) {
      return null;
    }

    try {
      setIsSpinning(true);

      const { data, error } = await supabase.rpc('spin_wheel', {
        p_user_id: user.id,
        p_use_gems: useGems,
        p_admin_test: adminTest
      });

      if (error) {
        debugLogger.error('WheelSpin', 'Error spinning wheel:', error);
        toast.error('Erro ao girar a roleta. Tente novamente.');
        return null;
      }

      const result = data as unknown as SpinResult;

      if (!result.success) {
        if (result.error === 'no_spins_remaining') {
          toast.error('Você não tem giros gratuitos restantes hoje');
        } else if (result.error === 'insufficient_gems') {
          toast.error('Você precisa de pelo menos 1 gema para girar');
        } else if (result.error === 'not_admin') {
          toast.error('Você não tem permissão para usar o modo admin');
        } else {
          toast.error(result.message || 'Erro ao girar a roleta');
        }
        return result;
      }

      // Atualizar spins info com o retorno (não atualiza em modo admin)
      if (result.spins_info) {
        setSpinsInfo(result.spins_info);
      }

      debugLogger.debug('WheelSpin', 'Spin result:', result);
      return result;
    } catch (error) {
      debugLogger.error('WheelSpin', 'Error spinning wheel:', error);
      toast.error('Erro ao girar a roleta. Tente novamente.');
      return null;
    } finally {
      setIsSpinning(false);
    }
  }, [user?.id, isSpinning]);

  const canSpin = spinsInfo ? (spinsInfo.free_spins_remaining > 0 || spinsInfo.can_buy_spin) : false;
  const hasFreeSpins = spinsInfo ? spinsInfo.free_spins_remaining > 0 : false;

  return {
    spinsInfo,
    prizes,
    isSpinning,
    loading,
    canSpin,
    hasFreeSpins,
    spin,
    fetchSpinsInfo,
    fetchPrizes
  };
};
